import React, { FunctionComponent, useContext, useEffect, useState } from "react";
import {
    ButtonProps,
    CommandBarButton,
    createErrorFailure,
    DefaultButton,
    ensure,
    FormValidationSummary,
    LoadingButton,
    Modal,
    Stack,
    Text,
    validationFailed,
    ValidationResult
} from "sprint-common-web";

import { MergeSeasonsRequest } from "../../../../api/MergeSeasonsRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { ValidateMergeSeasonsRequest } from "../../../../api/ValidateMergeSeasonsRequest";
import { TvSeriesPageContext } from "../../context/TvSeriesPageContext";
import { SeasonListingItem } from "../SeasonListingItem";

export const MergeSeasonsCommandBarButton: FunctionComponent<ButtonProps> = (props: ButtonProps) => {
    const { seasonsToMerge, clearSeasonsToMerge, updateListAfterMerge, tvSeries } = useContext(TvSeriesPageContext);
    ensure(tvSeries);
    const { mergeSeasons, validateMergeSeasons } = useAssetsManageTvSeriesApi();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!isModalOpen) return;

        setIsValidationLoading(true);

        validateMergeSeasons(tvSeries.id, createValidateMergeRequest(seasonsToMerge))
            .then((res) => setValidationResult(res))
            .catch((err) => setValidationResult(validationFailed([createErrorFailure(err)])))
            .finally(() => setIsValidationLoading(false));
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const isMergeButtonDisabled = () => {
        return seasonsToMerge.length !== 2;
    };

    const handleMergeButtonClick = async (): Promise<void> => {
        setIsLoading(true);
        const response = await mergeSeasons(tvSeries.id, createValidateMergeRequest(seasonsToMerge));
        await updateListAfterMerge(response.seasonId);
        closeModal();
        clearSeasonsToMerge();
        setIsLoading(false);
    };

    const isMergeConfirmDisabled = isLoading || isValidationLoading || !validationResult?.isValid;

    return (
        <>
            <CommandBarButton
                onClick={openModal}
                text="Merge"
                icon="Merge"
                disabled={isMergeButtonDisabled() || props.disabled}
            />
            <Modal isBlocking={true} isOpen={isModalOpen} headerText="Merge Seasons" onDismiss={closeModal}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text>You are about to Merge:</Text>
                    {seasonsToMerge.map((season: SeasonListingItem) => {
                        return <Text key={season.id}>{season.name}</Text>;
                    })}
                </Stack>
                <Stack tokens={{ padding: "10px 0" }}>
                    <FormValidationSummary validationResult={validationResult} isValidating={isValidationLoading} />
                </Stack>
                <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="end" horizontalAlign="end">
                    <DefaultButton text="Cancel" onClick={closeModal} disabled={isLoading} />
                    <LoadingButton
                        text="Merge"
                        isLoading={isLoading}
                        onClick={handleMergeButtonClick}
                        disabled={isMergeConfirmDisabled}
                    />
                </Stack>
            </Modal>
        </>
    );
};

const createValidateMergeRequest = (
    seasonItems: SeasonListingItem[]
): MergeSeasonsRequest | ValidateMergeSeasonsRequest => {
    return {
        firstSeasonId: seasonItems[0].id,
        secondSeasonId: seasonItems[1].id
    };
};
